import { useEffect, useRef, useState, useCallback } from 'react';
import { adminApi } from '../../api/admin.js';
import { api } from '../../api/client.js';

/**
 * Admin audit log viewer. Includes:
 *   - Summary bar (today, critical 7d, top actor, top resource)
 *   - Search + multi-select filters (severity, action, resource type)
 *   - Date range
 *   - Result table with severity pills, action badges, expandable change diffs
 *   - CSV export (server-side, includes the export itself as an audit entry)
 *   - Infinite scroll: loads 50 more on scroll-to-bottom
 */

const SEVERITY_COLOR = { info: '#64748b', warning: '#f59e0b', critical: '#dc2626' };
const ACTION_COLOR = {
  created: '#0d9488', updated: '#2563eb', deleted: '#dc2626',
  approved: '#16a34a', rejected: '#dc2626', exported: '#f59e0b',
  posted: '#9333ea', closed: '#dc2626', logged_in: '#64748b', login_failed: '#f59e0b',
  account_locked: '#dc2626',
};
const PAGE_SIZE = 50;

export default function AuditLog() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [filters, setFilters] = useState({
    q: '', user_id: '', actions: [], resource_types: [], severities: [], date_from: '', date_to: '',
  });
  const sentinelRef = useRef(null);

  // Initial summary fetch.
  useEffect(() => { adminApi.auditSummary().then(setSummary).catch(() => setSummary(null)); }, []);

  // Reload list whenever the filters change.
  const reload = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const params = serialize(filters);
      params.limit = PAGE_SIZE;
      params.offset = off;
      const r = await adminApi.audit(params);
      setItems(off === 0 ? (r?.items || []) : [...items, ...(r?.items || [])]);
      setTotal(r?.total || 0);
      setOffset(off);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, items]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  // Infinite scroll via IntersectionObserver.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && items.length < total) {
        reload(offset + PAGE_SIZE);
      }
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading, items.length, total, offset, reload]);

  function toggleExpand(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function clearFilters() {
    setFilters({ q: '', user_id: '', actions: [], resource_types: [], severities: [], date_from: '', date_to: '' });
  }

  async function downloadCsv() {
    const url = adminApi.auditExportUrl(serialize(filters));
    const r = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([r.data], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <section className="audit-log" data-testid="audit-log-page">
      <header className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Audit Log</h1>
        <div style={{ flex: 1 }} />
        <button className="btn ghost small" onClick={downloadCsv} data-testid="audit-export">Export CSV</button>
      </header>

      {/* Summary cards */}
      {summary && (
        <div data-testid="audit-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          <SummaryCard label="Today" value={summary.today_events} />
          <SummaryCard label="Critical (7d)" value={summary.critical_events_7d} accent={summary.critical_events_7d > 0 ? '#dc2626' : undefined} />
          <SummaryCard label="Top actor" value={summary.top_actors?.[0]?.user_name || summary.top_actors?.[0]?.user_email || '—'} small />
          <SummaryCard label="Top resource" value={summary.top_resources?.[0]?.resource_label || summary.top_resources?.[0]?.resource_type || '—'} small />
        </div>
      )}

      {/* Filter bar */}
      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <label style={{ flex: '1 1 220px' }}>
          Search
          <input
            data-testid="audit-search"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="actor, resource label, action…"
          />
        </label>
        <label>
          From
          <input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} />
        </label>
        <label>
          To
          <input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} />
        </label>
        <MultiPicker
          label="Severity"
          testId="audit-severity"
          options={['info', 'warning', 'critical']}
          value={filters.severities}
          onChange={(v) => setFilters((f) => ({ ...f, severities: v }))}
        />
        <MultiPicker
          label="Action"
          testId="audit-action"
          options={['created', 'updated', 'deleted', 'approved', 'rejected', 'logged_in', 'login_failed', 'account_locked', 'exported', 'posted', 'closed']}
          value={filters.actions}
          onChange={(v) => setFilters((f) => ({ ...f, actions: v }))}
        />
        <MultiPicker
          label="Resource"
          testId="audit-resource"
          options={['user', 'issue', 'leave_request', 'journal_entry', 'financial_period', 'wiki_page', 'audit_log']}
          value={filters.resource_types}
          onChange={(v) => setFilters((f) => ({ ...f, resource_types: v }))}
        />
        <button className="btn ghost small" onClick={clearFilters} data-testid="audit-clear">Clear</button>
      </div>

      {/* Result table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ maxHeight: '64vh', overflow: 'auto' }}>
          <table className="data-table" data-testid="audit-table" style={{ width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
              <tr>
                <th style={{ width: 30 }}></th>
                <th style={{ width: 130 }}>Time</th>
                <th>Actor</th>
                <th style={{ width: 130 }}>Action</th>
                <th>Resource</th>
                <th style={{ width: 140 }}>Changes</th>
                <th style={{ width: 90 }}>Severity</th>
                <th style={{ width: 110 }}>IP</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={8} className="muted center" style={{ padding: 32 }}>No matching audit entries.</td></tr>
              )}
              {items.map((a) => {
                const isExpanded = expanded.has(a.id);
                const changes = parseJson(a.changes);
                const changeCount = changes ? Object.keys(changes).length : 0;
                return (
                  <FragmentRow key={a.id}
                    a={a} isExpanded={isExpanded} changes={changes} changeCount={changeCount}
                    onToggle={() => toggleExpand(a.id)} />
                );
              })}
            </tbody>
          </table>
          <div ref={sentinelRef} style={{ height: 24 }} />
          {loading && <div className="muted center small" style={{ padding: 12 }}>Loading…</div>}
          {!loading && items.length >= total && total > 0 && (
            <div className="muted center small" style={{ padding: 12 }}>End of {total} entries.</div>
          )}
        </div>
      </div>
    </section>
  );
}

function FragmentRow({ a, isExpanded, changes, changeCount, onToggle }) {
  const sevColor = SEVERITY_COLOR[a.severity] || SEVERITY_COLOR.info;
  const actColor = ACTION_COLOR[a.action] || '#64748b';
  return (
    <>
      <tr data-testid={`audit-row-${a.id}`} style={{ borderTop: '1px solid #f1f5f9' }}>
        <td onClick={onToggle} style={{ cursor: 'pointer' }}>{isExpanded ? '▼' : '▶'}</td>
        <td className="muted small" title={a.created_at}>{relTime(a.created_at)}</td>
        <td style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 500 }}>{a.user_name || '—'}</div>
          <div className="muted small">{a.user_email || ''}</div>
        </td>
        <td>
          <span data-testid={`audit-action-${a.action}`} style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
            background: actColor + '22', color: actColor, textTransform: 'lowercase',
          }}>{a.action}</span>
        </td>
        <td style={{ fontSize: 12 }}>
          <span className="chip" style={{ fontSize: 10, marginRight: 4 }}>{a.resource_type || a.resource}</span>
          {a.resource_label || (a.resource_id ? <code className="muted small">{a.resource_id.slice(0, 12)}…</code> : '—')}
        </td>
        <td className="muted small">
          {changeCount > 0
            ? <button type="button" className="link-btn" onClick={onToggle} data-testid={`audit-changes-${a.id}`}>{changeCount} field{changeCount > 1 ? 's' : ''}</button>
            : '—'}
        </td>
        <td>
          <span data-testid={`audit-sev-${a.severity}`} style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
            background: sevColor + '22', color: sevColor, textTransform: 'uppercase',
          }}>{a.severity}</span>
        </td>
        <td className="muted small" style={{ fontFamily: 'monospace', fontSize: 11 }}>{a.ip || '—'}</td>
      </tr>
      {isExpanded && (
        <tr data-testid={`audit-detail-${a.id}`}>
          <td colSpan={8} style={{ background: '#f8fafc', padding: 12 }}>
            {changeCount > 0 && (
              <div style={{ marginBottom: 8 }}>
                <strong style={{ fontSize: 12 }}>Changes</strong>
                <table style={{ width: '100%', marginTop: 4, fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ color: '#64748b' }}><th align="left">Field</th><th align="left">Old</th><th align="left">New</th></tr></thead>
                  <tbody>
                    {Object.entries(changes).map(([k, v]) => (
                      <tr key={k} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{k}</td>
                        <td style={{ padding: '4px 8px', color: '#dc2626' }}><Value v={v?.old} /></td>
                        <td style={{ padding: '4px 8px', color: '#16a34a' }}><Value v={v?.new} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {a.metadata && Object.keys(parseJson(a.metadata) || {}).length > 0 && (
              <div>
                <strong style={{ fontSize: 12 }}>Metadata</strong>
                <pre style={{ margin: 4, padding: 8, background: '#fff', fontSize: 11, borderRadius: 4, overflow: 'auto' }}>
                  {JSON.stringify(parseJson(a.metadata), null, 2)}
                </pre>
              </div>
            )}
            {a.request_id && <div className="muted small" style={{ marginTop: 4 }}>Request ID: <code>{a.request_id}</code></div>}
          </td>
        </tr>
      )}
    </>
  );
}

function Value({ v }) {
  if (v == null) return <em className="muted">∅</em>;
  if (typeof v === 'object') return <code style={{ fontSize: 11 }}>{JSON.stringify(v)}</code>;
  return String(v);
}

function SummaryCard({ label, value, small, accent }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="muted small" style={{ textTransform: 'uppercase', letterSpacing: '.05em', fontSize: 10 }}>{label}</div>
      <div style={{ marginTop: 6, fontSize: small ? 14 : 22, fontWeight: 600, color: accent || '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </div>
    </div>
  );
}

function MultiPicker({ label, options, value, onChange, testId }) {
  function toggle(opt) {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  }
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
      <span className="small muted" style={{ fontSize: 11 }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
        {options.map((opt) => {
          const active = value.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              data-testid={`${testId}-opt-${opt}`}
              onClick={() => toggle(opt)}
              className="chip"
              style={{
                fontSize: 10,
                background: active ? '#0369a1' : '#e2e8f0',
                color: active ? '#fff' : '#475569',
                cursor: 'pointer', border: 'none',
                padding: '2px 8px', borderRadius: 12,
              }}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function serialize(filters) {
  const out = {};
  if (filters.q) out.q = filters.q;
  if (filters.user_id) out.user_id = filters.user_id;
  if (filters.severities?.length) out.severities = filters.severities.join(',');
  if (filters.actions?.length) out.actions = filters.actions.join(',');
  if (filters.resource_types?.length) out.resource_types = filters.resource_types.join(',');
  if (filters.date_from) out.date_from = filters.date_from;
  if (filters.date_to) out.date_to = filters.date_to;
  return out;
}

function parseJson(v) {
  if (v == null) return null;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch { return null; }
}

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
